import React, { useCallback, useEffect, useState } from 'react';
import api from './services/api';
import ArtistNavbar from './components/ArtistNavbar';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
  useOutletContext,
} from 'react-router-dom';

import Navbar from './components/Navbar';
import WorkflowBar from './components/WorkflowBar';
import useConsultationActionCounts from './hooks/useConsultationActionCounts';
import useCancellationNotices from './hooks/useCancellationNotices';
import CancellationCenter from './components/CancellationCenter';
import useConfirmationNotices from './hooks/useConfirmationNotices';
import ConfirmationCenter from './components/ConfirmationCenter';

import Home from './components/Home';
import AdminTools from './components/AdminTools';
import ConsultationView from './components/ConsultationView';
import DayPlan from './components/DayPlan';
import Insights from './components/Insights';
import Rebooking from './components/Rebooking';
import Vouchers from './components/Vouchers';
import GuestSpots from './components/GuestSpots';
import ArtistDashboard from './components/ArtistDashboard';
import AeonasChat from './components/AeonasChat';
import useChatPresence from './components/useChatPresence';

import {
  AuthProvider,
  useAuth,
} from './auth/AuthContext';

import ProtectedRoute from './auth/ProtectedRoute';
import LoginPage from './auth/LoginPage';
import ArtistInvite from './components/ArtistInvite';

const APP_PRESENCE = { channel:'app' };

const STAFF_ROLES = [
  'owner',
  'manager',
  'store_manager',
  'reception',
];

/* ===========================
   ROLE GUARDS
=========================== */

function StaffOnly({ children }) {
  const { user } = useAuth();

  if (user?.role === 'artist') {
    return <Navigate to="/artist" replace />;
  }

  if (!STAFF_ROLES.includes(user?.role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function ManagementOnly({ children }) {
  const { user } = useAuth();

  if (user?.role !== 'owner') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function ArtistOnly({ children }) {
  const { user } = useAuth();

  if (user?.role !== 'artist') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function RoleAwareFallback() {
  const { user } = useAuth();

  if (user?.role === 'artist') {
    return <Navigate to="/artist" replace />;
  }

  return <Navigate to="/" replace />;
}

/* ===========================
   MAIN LAYOUT
=========================== */

function AppLayout() {
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useChatPresence(APP_PRESENCE,true);
  const isArtist = user?.role === 'artist';
  const cancellationFeed = useCancellationNotices(!isArtist);
  const confirmationFeed = useConfirmationNotices(!isArtist);
  const actionCounts = useConsultationActionCounts(!isArtist, cancellationFeed.counts, confirmationFeed.counts);

  const [locations, setLocations] = useState([]);

  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    if (user?.role === 'artist' && user?.locationId) {
      return String(user.locationId);
    }

    return localStorage.getItem('selectedLocationId') || '';
  });

  const handleLocationChange = useCallback((id) => {
    if (isArtist) {
      return;
    }

    const nextLocationId = String(id);

    setSelectedLocationId(nextLocationId);

    localStorage.setItem(
      'selectedLocationId',
      nextLocationId
    );
  }, [isArtist]);

  /* ===========================
     LOAD LOCATIONS
  =========================== */

  useEffect(() => {
    if (isArtist) {
      setLocations([]);

      setSelectedLocationId(
        user?.locationId
          ? String(user.locationId)
          : ''
      );

      return;
    }

    const fetchLocations = async () => {
      try {
        const response = await api.get(
          '/api/locations'
        );

        const rows = Array.isArray(response.data)
          ? response.data
          : [];

        setLocations(rows);

        if (rows.length === 0) {
          return;
        }

        const savedLocationId =
          localStorage.getItem(
            'selectedLocationId'
          );

        const savedLocationExists = rows.some(
          (location) =>
            String(location.id) ===
            String(savedLocationId)
        );

        if (
          savedLocationId &&
          savedLocationExists
        ) {
          setSelectedLocationId(
            String(savedLocationId)
          );

          return;
        }

        const firstLocationId = String(
          rows[0].id
        );

        setSelectedLocationId(
          firstLocationId
        );

        localStorage.setItem(
          'selectedLocationId',
          firstLocationId
        );
      } catch (error) {
        console.error(
          'Error fetching locations:',
          error
        );
      }
    };

    fetchLocations();
  }, [
    isArtist,
    user?.locationId,
  ]);

  /* ===========================
     KEYBOARD SHORTCUTS
  =========================== */

  useEffect(() => {
    if (isArtist) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const activeElement =
        document.activeElement;

      const tag =
        activeElement?.tagName;

      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        activeElement?.isContentEditable
      ) {
        return;
      }

      switch (
      event.key.toLowerCase()
      ) {
        case 'e':
          handleLocationChange(1);
          break;

        case 'm':
          handleLocationChange(2);
          break;

        case 'd':
          handleLocationChange(3);
          break;

        case 'g':
          navigate('/vouchers');
          break;

        case 'c':
          navigate('/consultations');
          break;

        case 'b':
          navigate('/');
          break;

        case 'h':
          navigate('/dayplan');
          break;

        default:
          return;
      }

      event.preventDefault();
    };

    window.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [handleLocationChange, isArtist, navigate]);

  return (
    <>
      {isArtist && routeLocation.pathname === '/chat' && <ArtistNavbar />}
      {!isArtist && (
        <>
          <Navbar
            actionCounts={actionCounts}
            locations={locations}
            selectedLocationId={
              selectedLocationId
            }
            setSelectedLocationId={
              handleLocationChange
            }
          />

          <WorkflowBar actionCounts={actionCounts} />
          <CancellationCenter feed={cancellationFeed} />
          <ConfirmationCenter feed={confirmationFeed} />
        </>
      )}
      <Outlet
        context={{
          locations,
          selectedLocationId,
          setSelectedLocationId:
            handleLocationChange,
        }}
      />
    </>
  );
}

/* ===========================
   ROUTE WRAPPERS
=========================== */

function HomeRoute() {
  const {
    locations,
    selectedLocationId,
    setSelectedLocationId,
  } = useOutletContext();

  return (
    <Home
      locations={locations}
      selectedLocationId={
        selectedLocationId
      }
      setSelectedLocationId={
        setSelectedLocationId
      }
    />
  );
}

function AdminRoute() {
  const {
    selectedLocationId,
  } = useOutletContext();

  return (
    <AdminTools
      selectedLocationId={
        selectedLocationId
      }
    />
  );
}

function ConsultationsRoute() {
  const {
    locations,
    selectedLocationId,
    setSelectedLocationId,
  } = useOutletContext();

  return (
    <ConsultationView
      locations={locations}
      selectedLocationId={
        selectedLocationId
      }
      setSelectedLocationId={
        setSelectedLocationId
      }
    />
  );
}

function DayPlanRoute() {
  const {
    selectedLocationId,
  } = useOutletContext();

  return (
    <DayPlan
      selectedLocationId={
        selectedLocationId
      }
    />
  );
}

function RebookingRoute() {
  const {
    selectedLocationId,
  } = useOutletContext();

  return (
    <Rebooking
      selectedLocationId={
        selectedLocationId
      }
    />
  );
}

function InsightsRoute() {
  const {
    selectedLocationId,
  } = useOutletContext();

  return (
    <Insights
      selectedLocationId={
        selectedLocationId
      }
    />
  );
}

function VouchersRoute() {
  const {
    selectedLocationId,
  } = useOutletContext();

  return (
    <Vouchers
      selectedLocationId={
        selectedLocationId
      }
    />
  );
}

function GuestSpotsRoute() {
  const {
    locations,
    selectedLocationId,
  } = useOutletContext();

  return (
    <GuestSpots
      locations={locations}
      selectedLocationId={
        selectedLocationId
      }
    />
  );
}

function ChatRoute() {
  const { selectedLocationId, setSelectedLocationId } = useOutletContext();
  return <AeonasChat selectedLocationId={selectedLocationId} setSelectedLocationId={setSelectedLocationId} />;
}

function ArtistRoute() {
  return <ArtistDashboard />;
}

/* ===========================
   APP ROUTES
=========================== */

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route
            path="/artist-invite/:token" element={<ArtistInvite />} />
            <Route path="/login"
            element={<LoginPage />}
          />

          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/chat" element={<ChatRoute />} />
            <Route
              path="/artist"
              element={
                <ArtistOnly>
                  <>
                    <ArtistNavbar />
                    <ArtistRoute />
                  </>
                </ArtistOnly>
              }
            />

            <Route
              path="/"
              element={
                <StaffOnly>
                  <HomeRoute />
                </StaffOnly>
              }
            />

            <Route
              path="/admin"
              element={
                <ManagementOnly>
                  <AdminRoute />
                </ManagementOnly>
              }
            />

            <Route
              path="/consultations"
              element={
                <StaffOnly>
                  <ConsultationsRoute />
                </StaffOnly>
              }
            />

            <Route
              path="/dayplan"
              element={
                <StaffOnly>
                  <DayPlanRoute />
                </StaffOnly>
              }
            />

            <Route
              path="/rebooking"
              element={
                <StaffOnly>
                  <RebookingRoute />
                </StaffOnly>
              }
            />

            <Route
              path="/insights"
              element={
                <ManagementOnly>
                  <InsightsRoute />
                </ManagementOnly>
              }
            />

            <Route
              path="/vouchers"
              element={
                <StaffOnly>
                  <VouchersRoute />
                </StaffOnly>
              }
            />

            <Route
              path="/guest-spots"
              element={
                <StaffOnly>
                  <GuestSpotsRoute />
                </StaffOnly>
              }
            />

            <Route
              path="*"
              element={
                <RoleAwareFallback />
              }
            />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
// prime-ink-cancellation-alerts-v1
// prime-ink-confirmation-notifications-v1
